import { type CardRef, cardRefFromId } from "./card-ref.js";

export type DeckMeta = {
  tcglDeckId?: string;
  deckName?: string;
  deckDefinitionId?: string;
  size?: number;
  sleeve?: string;
  coin?: string;
  deckBox?: string;
};

export type PreambleDeck = {
  playerName: string;
  meta: DeckMeta;
  cards: CardRef[];
};

export type ParsedPreamble = {
  local?: PreambleDeck;
  opponent?: PreambleDeck;
  inferredPrizes?: CardRef[];
  prizeCardsTaken?: CardRef[];
  /** Unknown `[Tag] ...` lines, keyed by tag, so future debug output is never dropped. */
  extras: Record<string, string[]>;
};

const DECK_HEADER = /^\[MatchLoad\] (Local|Opponent) deck \((.*)\):\s*$/;
const DECK_SEARCH = /^\[DeckSearch\] Inferred prizes \((\d+) cards?\):(.*)$/;
const PRIZE_TAKEN = /^\[PrizeTaken\] (\d+)x (\S+)\s*$/;
const COUNT_LINE = /^\s+(\d+)x (\S+)\s*$/;
const TAGGED = /^\[([A-Za-z0-9_-]+)\]\s?(.*)$/;
const META_KEYS: Record<string, keyof DeckMeta> = {
  id: "tcglDeckId",
  deckName: "deckName",
  deckDefinitionID: "deckDefinitionId",
  size: "size",
  sleeve: "sleeve",
  coin: "coin",
  deckBox: "deckBox",
};

/** Parse the `=== Match Start ===` section written by the modified client. */
export function parsePreamble(text: string | undefined): ParsedPreamble {
  const result: ParsedPreamble = { extras: {} };
  if (text === undefined || text.trim().length === 0) return result;

  const lines = text.split("\n");
  let currentDeck: PreambleDeck | undefined;
  let inCards = false;
  let inPrizes: CardRef[] | undefined;

  for (const rawLine of lines) {
    const line = rawLine.replace(/\s+$/, "");
    if (line.length === 0) continue;

    const deckHeader = DECK_HEADER.exec(line);
    if (deckHeader !== null) {
      const side = deckHeader[1] === "Local" ? "local" : "opponent";
      currentDeck = { playerName: (deckHeader[2] ?? "").trim(), meta: {}, cards: [] };
      result[side] = currentDeck;
      inCards = false;
      inPrizes = undefined;
      continue;
    }

    const deckSearch = DECK_SEARCH.exec(line);
    if (deckSearch !== null) {
      inPrizes = [];
      result.inferredPrizes = inPrizes;
      currentDeck = undefined;
      inCards = false;
      continue;
    }

    const prizeTaken = PRIZE_TAKEN.exec(line);
    if (prizeTaken !== null) {
      const count = Number.parseInt(prizeTaken[1] ?? "1", 10);
      const list = result.prizeCardsTaken ?? [];
      list.push(cardRefFromId(prizeTaken[2] ?? "", count));
      result.prizeCardsTaken = list;
      currentDeck = undefined;
      inCards = false;
      inPrizes = undefined;
      continue;
    }

    const countLine = COUNT_LINE.exec(line);
    if (countLine !== null) {
      const ref = cardRefFromId(countLine[2] ?? "", Number.parseInt(countLine[1] ?? "0", 10));
      if (inPrizes !== undefined) {
        inPrizes.push(ref);
      } else if (currentDeck !== undefined && inCards) {
        currentDeck.cards.push(ref);
      }
      continue;
    }

    if (currentDeck !== undefined) {
      if (line === "DeckInfo") continue;
      if (line === "cards:") {
        inCards = true;
        continue;
      }
      const colon = line.indexOf(":");
      if (colon > 0 && !line.startsWith("[")) {
        const key = line.slice(0, colon);
        const value = line.slice(colon + 1).trim();
        const metaKey = META_KEYS[key];
        if (metaKey === "size") {
          const size = Number.parseInt(value, 10);
          if (!Number.isNaN(size)) currentDeck.meta.size = size;
        } else if (metaKey !== undefined && value.length > 0) {
          currentDeck.meta[metaKey] = value;
        }
        continue;
      }
    }

    const tagged = TAGGED.exec(line);
    if (tagged !== null) {
      const tag = tagged[1] ?? "unknown";
      const list = result.extras[tag] ?? [];
      list.push(tagged[2] ?? "");
      result.extras[tag] = list;
      currentDeck = undefined;
      inCards = false;
      inPrizes = undefined;
      continue;
    }

    const list = result.extras.unparsed ?? [];
    list.push(line);
    result.extras.unparsed = list;
  }

  return result;
}
